import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import type { AuthSession } from '../auth/firebaseAuth';
import { AuthError, resumeFromRefreshToken, signInWithPassword } from '../auth/firebaseAuth';
import { awaitGrant, describeThisBrowser, startPairing } from '../vault/pairingFlow';
import type { PairingSession } from '../vault/pairingFlow';
import { setSession } from '../session/vaultSession';
import { adoptFromPeer } from '../session/tabHandshake';
import { clearPersistedAccount, persistAccount, persistedRefreshToken, setAuth } from '../session/authSession';
import { completeGoogleSignIn, startGoogleSignIn } from '../auth/googleAuth';
import { hasRememberedBrowser, rememberBrowser, rememberedGuard, unlockBrowser } from '../vault/trustFlow';
import type { GuardChoice, GuardKind } from '../vault/trustedBrowser';
import { MIN_PASSPHRASE_CHARS } from '../vault/passphraseGuard';
import { PhraseForm } from '../vault/PhraseForm';
import { platformAuthenticatorAvailable } from '../vault/webauthnPrf';
import type { VaultSession } from '../session/vaultSession';

type Stage =
  | { name: 'returning' }
  | { name: 'signed-out' }
  | { name: 'unlocking'; auth: AuthSession }
  /** Remembered, but the fingerprint was declined or the share was out of
   *  reach. One button away, and nothing has been given up. */
  | { name: 'locked'; auth: AuthSession }
  /** Remembered behind a phrase: it has to be typed before anything opens. */
  | { name: 'phrase'; auth: AuthSession; wrong: boolean }
  /** Just paired, and the phone allowed this browser to be remembered: HOW is
   *  the question, and it is asked rather than answered here. `typing` reveals
   *  the phrase form. Holds the session it would seal. */
  | { name: 'keep'; auth: AuthSession; session: VaultSession; typing: boolean }
  | { name: 'starting'; auth: AuthSession }
  | { name: 'waiting'; auth: AuthSession; pairing: PairingSession }
  | { name: 'failed'; auth: AuthSession; message: string };

/**
 * Connect this browser.
 *
 * Not onboarding. The session key is memory-only, so this is what a returning
 * reader meets every time — furniture, and the first thing a big screen ever
 * shows. Two things follow: it wears the same chrome as the rest of the app
 * rather than looking like a landing page, and it explains itself in plain
 * words, because pointing a phone camera at a laptop is not self-evident the
 * fifth time either.
 */
export function ConnectRoute() {
  const [stage, setStage] = useState<Stage>({ name: 'returning' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remembered, setRemembered] = useState(false);
  const [guard, setGuard] = useState<GuardKind | null>(null);
  /** The remember choice in flight. Every choice is locked until it settles: a
   *  second click during a passkey prompt would enrol a second credential. */
  const [keeping, setKeeping] = useState<GuardKind | null>(null);
  /** Only decides whether to OFFER this device's own unlock. Whether it can
   *  actually hold a key is not knowable until one is enrolled. */
  const [platformAuth, setPlatformAuth] = useState(false);
  /** Why this browser is being asked to pair when it expected not to be. Kept
   *  separate from `error`: a dropped trust is news about the vault, not a
   *  failure of the form on screen. */
  const [notice, setNotice] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const navigate = useNavigate();
  useEffect(() => () => abort.current?.abort(), []);

  const beginPairing = useCallback(
    async (auth: AuthSession) => {
      setStage({ name: 'starting', auth });
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;

      try {
        const pairing = await startPairing(auth.idToken, describeThisBrowser());
        setStage({ name: 'waiting', auth, pairing });

        const outcome = await awaitGrant(auth.idToken, pairing, controller.signal);
        if (controller.signal.aborted) return;

        if (outcome.state === 'granted') {
          const session = { dataKey: outcome.grant.dataKey, grantSecret: outcome.grant.grantSecret };
          // The phone says WHETHER this browser may be remembered; how it is
          // kept is the person's to answer. Enrolling a passkey to find out
          // whether the platform does PRF is not a question that can be asked
          // silently: there is no way to test for it without creating a
          // credential, and no way to delete one that turns out to be useless.
          if (outcome.grant.persist) {
            return setStage({ name: 'keep', auth, session, typing: false });
          }
          setSession(session);
          // Straight into the cold load. No success screen — the board
          // filling in IS the confirmation.
          navigate('/', { replace: true });
        } else if (outcome.state === 'expired') {
          setStage({ name: 'failed', auth, message: 'That code expired.' });
        } else {
          setStage({
            name: 'failed',
            auth,
            message: 'The reply could not be opened — it was sealed for a different session.',
          });
        }
      } catch {
        if (!controller.signal.aborted) {
          setStage({ name: 'failed', auth, message: 'Could not reach the pairing service.' });
        }
      }
    },
    [navigate],
  );

  const tryUnlock = useCallback(
    async (auth: AuthSession, phrase?: string) => {
      setStage({ name: 'unlocking', auth });
      let outcome;
      try {
        outcome = await unlockBrowser(auth.idToken, phrase);
      } catch {
        return setStage({ name: 'locked', auth });
      }

      switch (outcome.kind) {
        case 'opened':
          setSession(outcome.session);
          return navigate('/', { replace: true });
        case 'needs-passphrase':
          return setStage({ name: 'phrase', auth, wrong: false });
        case 'wrong-passphrase':
          return setStage({ name: 'phrase', auth, wrong: true });
        case 'deferred':
          // Declined, or out of reach. The blob is still good, so this is one
          // button rather than a fall back to the phone.
          return setStage({ name: 'locked', auth });
        case 'refused':
          setRemembered(false);
          setNotice('Your phone cut this browser off, so the key it had stored is gone.');
          return beginPairing(auth);
        case 'unusable':
          setRemembered(false);
          setNotice('What this browser had stored could not be opened, so it has been dropped.');
          return beginPairing(auth);
        default:
          return beginPairing(auth);
      }
    },
    [beginPairing, navigate],
  );

  /** Signed in — so a sibling tab that is open hands its key over, or this
   *  browser is remembered and opens itself, or it asks the phone. A tab cut
   *  off while another was paired again comes here, and must not be sent to
   *  the phone a second time for a key one tab away. */
  const arrive = useCallback(
    async (auth: AuthSession) => {
      if (await adoptFromPeer()) return navigate('/', { replace: true });
      return (await hasRememberedBrowser()) ? tryUnlock(auth) : beginPairing(auth);
    },
    [beginPairing, navigate, tryUnlock],
  );

  /** Read before anything is signed in, because the copy on this screen is
   *  different for a browser that already holds a key — and different again
   *  depending on what will be asked for to open it. */
  useEffect(() => {
    void hasRememberedBrowser().then(setRemembered);
    void rememberedGuard().then(setGuard);
    void platformAuthenticatorAvailable().then(setPlatformAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    completeGoogleSignIn()
      .then(async (auth) => {
        if (cancelled) return;
        if (auth) {
          setAuth(auth);
          return arrive(auth);
        }
        // A reload drops everything this tab held, so a remembered browser would
        // otherwise ask to be signed in by hand every time. The account is
        // resumed from the token stored alongside the trust record — which is
        // why a browser nobody remembered still lands on the form.
        //
        // A refused token means this browser was cut off while it was away. The
        // stored copy is then rubbish and goes, rather than being retried on
        // every load forever.
        const stored = persistedRefreshToken();
        if (stored) {
          try {
            const resumed = await resumeFromRefreshToken(stored);
            if (cancelled) return;
            setAuth(resumed);
            return arrive(resumed);
          } catch {
            clearPersistedAccount();
            if (cancelled) return;
          }
        }
        setStage({ name: 'signed-out' });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setStage({ name: 'signed-out' });
        setError(e instanceof Error ? e.message : 'Google sign-in did not complete.');
      });
    return () => {
      cancelled = true;
    };
  }, [arrive]);

  /** Seals what the pairing already handed over, under the chosen guard. */
  const keepWith = useCallback(
    async (auth: AuthSession, session: VaultSession, choice: GuardChoice) => {
      setError(null);
      setKeeping(choice.kind);
      const kept = await rememberBrowser(auth.idToken, session, auth.email ?? '', choice).catch(
        () => ({ kind: 'unavailable' as const }),
      );
      setKeeping(null);
      // A passkey that answered without doing PRF is the one failure that leaves
      // something behind: the credential exists by the time we can test it, and
      // WebAuthn has no way to take it back. Saying so beats leaving litter in
      // someone's password manager with no explanation.
      if (kept.kind === 'needs-passphrase') {
        return setError(
          'That passkey cannot hold a key — it answered, but without the extension this needs. ' +
            'Your password manager may have saved one anyway; it is unused here and safe to delete. ' +
            'Pick one of the other two.',
        );
      }
      if (kept.kind === 'declined') {
        return setError('Nothing was stored. Pick again, or go in without being remembered.');
      }
      if (kept.kind !== 'remembered') {
        // Stays put rather than navigating with a message nobody would see. The
        // pass already works, so the choice on offer is to try again or go in
        // without being remembered.
        return setError('This browser could not be remembered. You can still go in for this session.');
      }
      // Only now: the account is stored because the browser is remembered, and
      // the two are dropped together when it is forgotten.
      persistAccount(auth);
      setSession(session);
      navigate('/', { replace: true });
    },
    [navigate],
  );

  /** Declining the phrase is a real answer: the pass already works for now. */
  const stayForThisSession = useCallback(
    (session: VaultSession) => {
      setSession(session);
      navigate('/', { replace: true });
    },
    [navigate],
  );

  const onSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const auth = await signInWithPassword(
        String(form.get('email') ?? ''),
        String(form.get('password') ?? ''),
      );
      setAuth(auth);
      await arrive(auth);
    } catch (e) {
      setError(e instanceof AuthError ? e.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  if (stage.name === 'returning') {
    return <div className="arrive" />;
  }

  return (
    <div className="arrive">
      <div className="arrive-left">
        <p className="t-eyebrowMono arrive-eyebrow">
          {remembered ? 'unlock this browser' : 'connect this browser'}
        </p>
        <h1 className="t-pageTitle">
          {remembered ? 'This browser remembers your key.' : 'Your phone holds the only key.'}
        </h1>
        {/* Names what will be asked for, because "remembered" alone hides the
            difference between a key behind this device's unlock and one behind
            a phrase — and that difference is the whole security story. */}
        <p className="arrive-body">
          {!remembered
            ? 'This page can knock on your vault, but only your phone can open it. Scanning lets this browser in, and your phone decides whether it is remembered here or only until you close it.'
            : guard === 'passphrase'
              ? 'It is stored sealed under your unlock phrase — no phone, no code. Your phone can cut it off at any time, and then what is stored here stops working.'
              : guard === 'none'
                ? 'It is stored sealed here and opens without asking — nothing on this machine gates it; being signed in is what does. Your phone can cut it off at any time, and then what is stored here stops working.'
                : 'It is stored sealed, and opening it takes this device’s own unlock — no phone, no code. Your phone can cut it off at any time, and then what is stored here stops working.'}
        </p>
        {notice && <p className="arrive-notice">{notice}</p>}

        {/* Step 2 used to name a "Menu → Connect a browser" item that does not
            exist in the app. The camera is the whole path: it resolves the
            link, which opens Pondering on the approval screen. */}
        {stage.name === 'signed-out' ? (
          <SignIn onSubmit={onSignIn} busy={busy} error={error} />
        ) : stage.name === 'unlocking' ? (
          <p className="t-metaMono arrive-waiting">
            <span className="waiting-dot" /> opening this browser&rsquo;s key…
          </p>
        ) : stage.name === 'locked' ? (
          <div className="stack">
            <button type="button" className="btn-primary" onClick={() => void tryUnlock(stage.auth)}>
              Unlock
            </button>
            <p className="t-metaMono arrive-or">
              or <button type="button" className="linkish" onClick={() => void beginPairing(stage.auth)}>
                use your phone instead
              </button>
            </p>
          </div>
        ) : stage.name === 'phrase' ? (
          <PhraseForm
            label="Your unlock phrase"
            submit="Unlock"
            account={stage.auth.email}
            error={stage.wrong ? 'That phrase did not open it. Nothing was lost — try again.' : null}
            onSubmit={(phrase) => void tryUnlock(stage.auth, phrase)}
            aside={
              <p className="t-metaMono arrive-or">
                or <button type="button" className="linkish" onClick={() => void beginPairing(stage.auth)}>
                  use your phone instead
                </button>
              </p>
            }
          />
        ) : stage.name === 'keep' ? (
          stage.typing ? (
            <PhraseForm
              label="Unlock phrase"
              submit="Remember, behind this phrase"
              busy={keeping === 'passphrase' ? 'Remembering…' : null}
              account={stage.auth.email}
              confirm
              error={error}
              hint={`Asked for on every later visit. At least ${MIN_PASSPHRASE_CHARS} characters, and not recoverable — forget it and you pair with your phone again. Your password manager can hold it.`}
              onSubmit={(phrase) => void keepWith(stage.auth, stage.session, { kind: 'passphrase', phrase })}
              aside={
                <p className="t-metaMono arrive-or">
                  or <button
                    type="button"
                    className="linkish"
                    disabled={keeping !== null}
                    onClick={() => setStage({ ...stage, typing: false })}
                  >
                    back
                  </button>
                </p>
              }
            />
          ) : (
            <div className="arrive-signin">
              {/* The trade, in one line each, because this is the moment to say
                  it and the phone's session list is where it is said again. */}
              <p className="arrive-note phrase-hint">
                What should opening it here ask for? This device&rsquo;s own unlock is the
                strongest — the key never leaves the authenticator. A phrase is asked for on every
                visit. Trusting the browser asks for nothing: what is stored cannot be read on its
                own, but nothing on this machine gates it.
              </p>
              {error && <p className="error">{error}</p>}
              <div className="stack">
                {platformAuth && (
                  <KeepButton
                    primary
                    keeping={keeping}
                    kind="hardware"
                    pending="Waiting for this device…"
                    onClick={() => void keepWith(stage.auth, stage.session, { kind: 'hardware' })}
                  >
                    Use this device&rsquo;s unlock
                  </KeepButton>
                )}
                <KeepButton
                  primary={!platformAuth}
                  keeping={keeping}
                  kind="passphrase"
                  onClick={() => setStage({ ...stage, typing: true })}
                >
                  Secure it with a phrase
                </KeepButton>
                <KeepButton
                  keeping={keeping}
                  kind="none"
                  pending="Remembering…"
                  onClick={() => void keepWith(stage.auth, stage.session, { kind: 'none' })}
                >
                  Trust this browser
                </KeepButton>
              </div>
              <p className="t-metaMono arrive-or">
                or <button
                  type="button"
                  className="linkish"
                  disabled={keeping !== null}
                  onClick={() => stayForThisSession(stage.session)}
                >
                  don&rsquo;t remember this browser
                </button>
              </p>
            </div>
          )
        ) : (
          <ol className="arrive-steps">
            <Step n={1}>Open your phone&rsquo;s camera — the ordinary one</Step>
            <Step n={2}>Point it at this code</Step>
            <Step n={3}>
              Pondering opens and asks whether to <strong>let this browser in</strong>
            </Step>
          </ol>
        )}

        {/* No duration claimed: the pass's length was set on the phone, and
            this screen has not asked the backend for it. */}
        <p className="t-metaMono arrive-foot">
          {!remembered
            ? 'nothing stored here yet'
            : guard === 'passphrase'
              ? 'remembered · behind your phrase'
              : guard === 'none'
                ? 'remembered · nothing asked'
                : 'remembered · behind this device'}
        </p>
      </div>

      {/* Nothing on the right until there is a code, or one on the way. An
          empty plate before sign-in shows a slot for something that is not
          coming yet, which reads as a broken image rather than a next step. */}
      {(stage.name === 'starting' || stage.name === 'waiting' || stage.name === 'failed') && (
        <div className="arrive-right">
          {stage.name === 'waiting' && (
            <>
              <QrPlate text={stage.pairing.link} />
              <p className="arrive-account">{stage.auth.email}</p>
              <p className="t-metaMono arrive-waiting">
                <span className="waiting-dot" /> waiting for your phone…
              </p>
              {/* States the constraint so a future "copy link" button reads as
                  the contradiction it would be: the fragment carrying the key
                  never reaches a server, and the camera is what keeps it that
                  way. */}
              <p className="arrive-note">
                There is no link to copy, on purpose. The code only travels through your camera, so
                it never reaches a server. Pondering doesn&rsquo;t need to be open on the phone — the
                link opens it.
              </p>
            </>
          )}
          {stage.name === 'starting' && (
            <>
              <QrPlate text={null} />
              <p className="t-metaMono arrive-waiting">
                <span className="waiting-dot" /> preparing a code…
              </p>
            </>
          )}
          {stage.name === 'failed' && (
            <div className="arrive-failed">
              <p className="error">{stage.message}</p>
              <button type="button" onClick={() => void beginPairing(stage.auth)}>
                New code
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One form for both phrase moments — setting one after pairing, and typing it on
 * a later visit. `confirm` is what distinguishes them: a phrase being SET has no
 * way to be checked later, so it is typed twice; one being USED is checked by
 * whether the vault opens.
 */

function SignIn({
  onSubmit,
  busy,
  error,
}: {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div className="arrive-signin">
      <button type="button" className="btn-primary" onClick={() => startGoogleSignIn()}>
        Continue with Google
      </button>
      <p className="t-metaMono arrive-or">or with an email and password</p>
      <form onSubmit={onSubmit} className="stack">
        <input name="email" type="email" placeholder="email" autoComplete="username" required />
        <input
          name="password"
          type="password"
          placeholder="password"
          autoComplete="current-password"
          required
        />
        <button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Near-white in both themes so the camera has contrast — a QR washed into a
 *  dark surface is a QR that takes three tries to scan. SVG rather than a
 *  canvas, which Firefox-family browsers can blank as fingerprinting
 *  protection. */
function QrPlate({ text }: { text: string | null }) {
  const path = useMemo(() => (text ? qrPath(text) : null), [text]);

  return (
    <div className={`qr-plate${path ? '' : ' empty'}`}>
      {path ? (
        <svg
          width={210}
          height={210}
          viewBox={`0 0 ${path.size} ${path.size}`}
          shapeRendering="crispEdges"
          role="img"
          aria-label="Pairing code"
        >
          <path d={path.d} fill="#000" />
        </svg>
      ) : (
        <span className="qr-pending" />
      )}
    </div>
  );
}

function qrPath(text: string): { size: number; d: string } {
  const { modules } = QRCode.create(text);
  let d = '';
  for (let row = 0; row < modules.size; row++) {
    for (let col = 0; col < modules.size; col++) {
      if (modules.get(row, col)) d += `M${col} ${row}h1v1h-1z`;
    }
  }
  return { size: modules.size, d };
}

function KeepButton({
  kind,
  keeping,
  primary = false,
  pending,
  onClick,
  children,
}: {
  kind: GuardKind;
  keeping: GuardKind | null;
  primary?: boolean;
  pending?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const mine = keeping === kind;
  return (
    <button
      type="button"
      className={primary ? 'btn-primary' : undefined}
      disabled={keeping !== null}
      aria-busy={mine}
      onClick={onClick}
    >
      {mine && pending ? (
        <>
          <span className="waiting-dot" /> {pending}
        </>
      ) : (
        children
      )}
    </button>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li>
      <span className="t-metaMono step-n">{n}</span>
      <span>{children}</span>
    </li>
  );
}
